import { LeftOutlined, QuestionCircleOutlined, RightOutlined } from "@ant-design/icons";
import { Card, Col, Row, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { profitApi } from "api/profit.api";
import { AppButton, AppTable } from "components/antd-ui";
import { TableContent, TableLayout, TableLayoutFooter, TableTotals } from "components/table";
import ReactECharts from "echarts-for-react";
import { useClientPaging } from "hooks/useClientPaging";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProfitMonthlyPoint } from "types/expense";
import { formatVND, getTitle } from "utils";
import { barValueLabel } from "utils/chart";
import dayjs, { Dayjs } from "utils/dayjs";

const formatMoney = (num: number) => `${formatVND(Math.round(num || 0))}đ`;

const profitColor = (value: number) => (value < 0 ? "text-red-600" : "text-green-600");

export const ProfitPage = observer(({ title = "" }) => {
  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const [rows, setRows] = useState<ProfitMonthlyPoint[]>([]);
  const [loading, setLoading] = useState(false);

  const year = monthValue.year();
  const month = monthValue.month() + 1;
  const now = dayjs();
  const isCurrentMonth = monthValue.isSame(now, "month");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchData = useCallback(async (y: number) => {
    setLoading(true);
    try {
      const res = await profitApi.findAll(y);
      setRows(res.data.rows || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(year);
  }, [fetchData, year]);

  const current = rows.find((row) => row.month === month);

  // Chỉ hiện các tháng đã tới: năm hiện tại đến tháng hiện tại, năm trước đủ 12 tháng, năm sau không có tháng nào
  const visibleRows = useMemo(() => {
    const today = dayjs();
    return rows.filter((row) => year < today.year() || (year === today.year() && row.month <= today.month() + 1));
  }, [rows, year]);

  const tableRows = useMemo(() => [...visibleRows].sort((a, b) => b.month - a.month), [visibleRows]);

  const paging = useClientPaging(tableRows);

  const yearTotal = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => ({
          revenue: acc.revenue + row.revenue,
          expense: acc.expense + row.expense,
          profit: acc.profit + row.profit,
        }),
        { revenue: 0, expense: 0, profit: 0 },
      ),
    [visibleRows],
  );

  const chartOption = useMemo(
    () => ({
      grid: { left: 110, right: 24, top: 48, bottom: 32 },
      legend: { top: 0 },
      tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
      xAxis: { type: "category", data: visibleRows.map((row) => `Tháng ${row.month}`) },
      yAxis: { type: "value", axisLabel: { formatter: (value: number) => formatVND(value) } },
      series: [
        { name: "Doanh thu", type: "bar", data: visibleRows.map((row) => row.revenue), itemStyle: { color: "#1677ff" }, barMaxWidth: 24, label: barValueLabel("top", 10) },
        { name: "Chi phí", type: "bar", data: visibleRows.map((row) => row.expense), itemStyle: { color: "#ef4444" }, barMaxWidth: 24, label: barValueLabel("top", 10) },
        { name: "Lợi nhuận", type: "bar", data: visibleRows.map((row) => row.profit), itemStyle: { color: "#22c55e" }, barMaxWidth: 24, label: barValueLabel("top", 10) },
      ],
    }),
    [visibleRows],
  );

  const columns = useMemo<TableColumnsType<ProfitMonthlyPoint>>(
    () => [
      {
        title: "Tháng",
        dataIndex: "month",
        key: "month",
        render: (value) => <TableContent>{`Tháng ${value}/${year}`}</TableContent>,
      },
      { title: "Doanh thu", dataIndex: "revenue", key: "revenue", align: "right", render: (value) => formatMoney(value) },
      { title: "Chi phí", dataIndex: "expense", key: "expense", align: "right", render: (value) => formatMoney(value) },
      {
        title: "Lợi nhuận",
        dataIndex: "profit",
        key: "profit",
        align: "right",
        render: (value) => <span className={`font-semibold ${profitColor(value)}`}>{formatMoney(value)}</span>,
      },
    ],
    [year],
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex items-center gap-1">
          <AppButton tone="light" aria-label="Tháng trước" onClick={() => setMonthValue((prev) => prev.subtract(1, "month"))}>
            <LeftOutlined />
          </AppButton>
          <div className="min-w-[110px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
          <AppButton tone="light" aria-label="Tháng sau" disabled={isCurrentMonth} onClick={() => setMonthValue((prev) => prev.add(1, "month"))}>
            <RightOutlined />
          </AppButton>
        </div>

        <Row gutter={16} className="mt-4">
          <Col span={8}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Doanh thu</div>
            <div className="text-xl font-bold mt-1 text-blue-600">{formatMoney(current?.revenue || 0)}</div>
          </Col>
          <Col span={8}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Chi phí</div>
            <div className="text-xl font-bold mt-1 text-red-600">{formatMoney(current?.expense || 0)}</div>
          </Col>
          <Col span={8}>
            <div className="text-xs text-gray-400 font-semibold uppercase flex items-center gap-1">
              Lợi nhuận
              <Tooltip title="Lợi nhuận = Doanh thu thực thu (công nợ đã thu, gồm VAT) − Chi phí thực tế (khoản chi Đã thanh toán). Tính trực tiếp từ dữ liệu, không lưu riêng.">
                <QuestionCircleOutlined className="text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className={`text-xl font-bold mt-1 ${profitColor(current?.profit || 0)}`}>{formatMoney(current?.profit || 0)}</div>
          </Col>
        </Row>
      </Card>

      <TableLayout
        tabs={
          <TableTotals
            items={[
              { label: `Cả năm ${year} — Doanh thu`, value: formatMoney(yearTotal.revenue), tone: "primary" },
              { label: "Chi phí", value: formatMoney(yearTotal.expense), tone: "negative" },
              {
                label: "Lợi nhuận",
                value: formatMoney(yearTotal.profit),
                tone: yearTotal.profit < 0 ? "negative" : "positive",
              },
            ]}
          />
        }
        table={
          <AppTable<ProfitMonthlyPoint>
            rowKey="month"
            size="small"
            compact
            loading={loading}
            pagination={false}
            dataSource={paging.pageRows}
            columns={columns}
            rowClassName={(row) => (row.month === month ? "ant-table-row-selected" : "")}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />

      <Card>
        <div className="font-semibold mb-2">Biểu đồ năm {year}</div>
        <ReactECharts option={chartOption} style={{ height: 420 }} showLoading={loading} />
      </Card>

    </div>
  );
});
